import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaTimes, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { employeesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { calculateDuration } from '../../../utils/dateUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import { ORGANIZATIONAL_STATUSES } from '../../../constants/organizationalStatuses';
import { ROLE_TYPES } from '../../../constants/roleTypes';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  BackButton,
  HeaderButton,
} from '../../common/styles';
import { Content, CurrentPositionContainer, TimelineContainer } from './styles';
import dayjs from 'dayjs';
import EmptyState from '../../common/EmptyState';
import ConfirmationModal from '../../common/ConfirmationModal';
import AddRoleModal from './AddRoleModal';
import EndRoleModal from './EndRoleModal';
import EditRoleModal from './EditRoleModal';
import { formatDateOnly } from '../../../utils/dateOnly';

const EmployeeRoleHistory = ({ onBack, scrollState, onEmployeeUpdate }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);

  // Modal states
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showEndRoleModal, setShowEndRoleModal] = useState(false);
  const [selectedRoleToEnd, setSelectedRoleToEnd] = useState(null);

  // Edit/Delete state
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [deleteRoleConfirmOpen, setDeleteRoleConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Use organizationalStatus from navigation state if available, otherwise from API response
  const organizationalStatus = location.state?.organizationalStatus || employeeData?.employee?.organizationalStatus;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [rolesData, historyData, positionData] = await Promise.all([
        organizationApi.getAllRoles(),
        employeesApi.getEmployeeRoleHistory(id),
        employeesApi.getEmployeePositionHistory(id)
      ]);

      setEmployeeData(historyData);
      setRoles(rolesData.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL));
      setCurrentPosition(positionData?.employee?.currentPosition || null);
    } catch (err) {
      showAlert(err.message || 'Failed to load data', 'error', err);
      if (onBack) {
        onBack();
      } else {
        navigate('/employees');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndRoleClick = (role) => {
    setSelectedRoleToEnd(role);
    setShowEndRoleModal(true);
  };

  const handleRoleSuccess = () => {
    fetchData();
    if (onEmployeeUpdate) onEmployeeUpdate();
  };

  // Edit/Delete handlers
  const handleEditRole = (entry) => {
    setRoleToEdit(entry);
    setEditRoleModalOpen(true);
  };

  const handleDeleteRoleClick = (entry) => {
    setRoleToDelete(entry);
    setDeleteRoleConfirmOpen(true);
  };

  const handleDeleteRoleConfirm = async () => {
    try {
      await employeesApi.deleteRoleHistory(id, roleToDelete.id);
      showAlert('Role history deleted successfully', 'success');
      setDeleteRoleConfirmOpen(false);
      setRoleToDelete(null);
      fetchData();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (err) {
      showAlert(err.message || 'Failed to delete role history', 'error', err);
    }
  };

  const handleRoleEditSuccess = () => {
    fetchData();
    if (onEmployeeUpdate) onEmployeeUpdate();
  };

  // Get current organizational role IDs
  const currentRoleIds = employeeData?.employee?.currentRoles
    ?.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
    ?.map(role => role.id) || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => {
            if (onBack) {
              onBack();
            } else {
              navigate(`/employees/${id}/edit`);
            }
          }}>
            <FaArrowLeft /> Back to Individual
          </BackButton>
          <Title>Organizational Roles History | {employeeData?.employee?.fullName}</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowAddRoleModal(true)}>
          <FaPlus /> Add Organizational Role
        </HeaderButton>
      </Header>

      <Content>
        {/* Current Roles */}
        <CurrentPositionContainer>
          <Typography variant="h6" sx={{ color: '#1E293B', fontWeight: 600, mb: 2 }}>
            Current Organizational Roles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {employeeData?.employee?.currentRoles?.filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)?.length > 0 ? (
              employeeData.employee.currentRoles
                .filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
                .map(role => (
                  <Box
                    key={role.id}
                    sx={{
                      backgroundColor: '#F1F5F9',
                      padding: '8px 8px 8px 16px',
                      borderRadius: '4px',
                      color: '#1E293B',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {role.label}
                    <Tooltip title="End this role">
                      <IconButton
                        size="small"
                        onClick={() => handleEndRoleClick(role)}
                        sx={{
                          p: 0.5,
                          color: '#64748B',
                          '&:hover': {
                            color: '#DC2626',
                            backgroundColor: 'rgba(220, 38, 38, 0.1)'
                          }
                        }}
                      >
                        <FaTimes size={12} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))
            ) : (
              <Typography sx={{ color: '#64748B' }}>No organizational roles assigned</Typography>
            )}
          </Box>
        </CurrentPositionContainer>

        {/* History Timeline */}
        <TimelineContainer>
          <Typography variant="h6" sx={{ mb: 3, color: '#1E293B', fontWeight: 600 }}>
            History
          </Typography>
          {!employeeData?.history?.filter(entry => entry.role.type === ROLE_TYPES.ORGANIZATIONAL)?.length ? (
            <EmptyState
              title="No History Found"
              description="No role history entries found for this individual."
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              maxWidth: '500px'
            }}>
              {employeeData.history
                .filter(entry => entry.role.type === ROLE_TYPES.ORGANIZATIONAL)
                .map((entry) => (
                  <Paper
                    key={entry.id}
                    sx={{
                      p: 3,
                      borderLeft: `4px solid ${!entry.endDate ? '#22C55E' : '#2563EB'}`,
                      backgroundColor: '#FFFFFF'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                          {entry.role.label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatDateOnly(entry.startDate, 'DD/MM/YYYY')} - {entry.endDate ? formatDateOnly(entry.endDate, 'DD/MM/YYYY') : 'Present'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                          {calculateDuration(entry.startDate, entry.endDate)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94A3B8', mt: 1 }}>
                          Updated by {entry.updatedBy} on {dayjs(entry.updatedAt).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                        <Tooltip title="Edit role">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRole(entry)}
                            sx={{
                              p: 0.5,
                              color: '#64748B',
                              '&:hover': { color: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.1)' }
                            }}
                          >
                            <FaEdit size={14} />
                          </IconButton>
                        </Tooltip>
                        {entry.endDate && (
                          <Tooltip title="Delete role">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoleClick(entry)}
                              sx={{
                                p: 0.5,
                                color: '#64748B',
                                '&:hover': { color: '#DC2626', backgroundColor: 'rgba(220, 38, 38, 0.1)' }
                              }}
                            >
                              <FaTrash size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
            </Box>
          )}
        </TimelineContainer>
      </Content>

      {/* Add Role Modal */}
      <AddRoleModal
        isOpen={showAddRoleModal}
        onClose={() => setShowAddRoleModal(false)}
        employeeId={id}
        currentPosition={currentPosition}
        currentRoleIds={currentRoleIds}
        onSuccess={handleRoleSuccess}
      />

      {/* End Role Modal */}
      <EndRoleModal
        isOpen={showEndRoleModal}
        onClose={() => {
          setShowEndRoleModal(false);
          setSelectedRoleToEnd(null);
        }}
        role={selectedRoleToEnd}
        employeeId={id}
        onSuccess={handleRoleSuccess}
      />

      {/* Edit Role Modal */}
      <EditRoleModal
        isOpen={editRoleModalOpen}
        onClose={() => {
          setEditRoleModalOpen(false);
          setRoleToEdit(null);
        }}
        employeeId={id}
        roleEntry={roleToEdit}
        onSuccess={handleRoleEditSuccess}
      />

      {/* Delete Role Confirmation */}
      <ConfirmationModal
        isOpen={deleteRoleConfirmOpen}
        onClose={() => {
          setDeleteRoleConfirmOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleDeleteRoleConfirm}
        title="Delete Role History"
        message={`Are you sure you want to delete the role "${roleToDelete?.role?.label}"?`}
        actionButtonText="Delete"
        actionButtonColor="#DC2626"
      />
    </Container>
  );
};

export default EmployeeRoleHistory; 